import { makeAutoObservable, runInAction } from 'mobx';
import { CreateOrderUseCase } from '@/core/application/use-cases/order/CreateOrderUseCase';
import { IContainer } from '@/shared/config/Container';
import { PaymentType } from '@/models/payment-plan';
import { CreateCheckoutSessionUseCase } from '@/core/application/use-cases/checkout/CreateCheckoutSessionUseCase';
import {
  CheckoutSessionParams,
  CheckoutSessionResult,
  CheckoutSessionService,
  CurrentCheckoutSession,
} from '@/core/infrastructure/services/ApiPaymentService';
import {
  PaymentPlanDetails,
  ProductPaymentPlanResponse,
} from '@/core/domain/entities/Product';
import { IProductRepository } from '@/core/domain/repositories/IProductRepository';

export interface CheckoutItem {
  id: string;
  productId: string;
  productName: string;
  productImage: string;
  price: number;
  quantity: number;
  subtotal: number;
  selectedOptions?: Record<string, any>;
}

export interface PaymentBreakdown {
  id: string;
  price: number;
  plan_amount: number;
  payment_type: string;
  keepMyPrice: boolean;
  date: string;
  due_date?: string;
  installment_number?: number;
  total_installments?: number;
}

export interface PaymentPlan {
  type: PaymentType;
  totalAmount: number;
  frequency?: 'weekly' | 'monthly' | 'daily';
  duration?: number;
  installmentAmount?: number;
  totalInstallments?: number;
  processingFee?: number;
  breakdown?: PaymentBreakdown[];
  keepMyPrice?: boolean;
}

export interface WorkDetails {
  employerName: string;
  workAddress: string;
  position: string;
  salary: number;
  workPhone: string;
  employmentType: 'full-time' | 'part-time' | 'contract' | 'self-employed';
  yearsOfEmployment: number;
}

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  region: string;
  postalCode?: string;
  country: string;
  phone: string;
}

export interface CheckoutSummary {
  subtotal: number;
  shipping: number;
  tax: number;
  discount: number;
  processingFee: number;
  total: number;
  currency: string;
}

export interface CheckoutValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

class CheckoutStore {
  // Observable state
  isLoading = false;
  error: string | null = null;

  // Checkout data
  items: CheckoutItem[] = [];
  selectedPaymentPlan: PaymentPlan | null = null;
  selectedPaymentMethod: any | null = null;
  workDetails: WorkDetails | null = null;
  shippingAddress: ShippingAddress | null = null;
  checkoutSession: CheckoutSessionResult | null = null;
  currentCheckoutSession: CurrentCheckoutSession | null = null;
  // Checkout state
  termsAccepted = false;
  privacyAccepted = false;
  marketingOptIn = false;

  // Order processing
  isCalculatingBreakdown = false;
  isCreatingOrder = false;
  isFinalizingOrder = false;
  createdOrder: any | null = null;

  // Available options
  availablePaymentPlans: PaymentPlan[] = [];
  availableFrequencies: string[] = ['weekly', 'monthly'];

  paymentPlanResults: ProductPaymentPlanResponse | null = null;

  // Use cases
  private createOrderUseCase: CreateOrderUseCase;
  private createCheckoutSessionUseCase: CreateCheckoutSessionUseCase;

  constructor(
    private container: IContainer,
    private readonly productRepository: IProductRepository,
    private readonly checkoutService: CheckoutSessionService
  ) {
    makeAutoObservable(this);
    this.createOrderUseCase =
      container.get<CreateOrderUseCase>('CreateOrderUseCase');
    this.createCheckoutSessionUseCase =
      container.get<CreateCheckoutSessionUseCase>(
        'CreateCheckoutSessionUseCase'
      );
    this.initializeDefaultPlans();
  }

  // Computed properties
  get subtotal(): number {
    return this.items.reduce((sum, item) => sum + item.subtotal, 0);
  }

  get shipping(): number {
    // Free shipping for orders over GHS 500
    return this.subtotal >= 500 ? 0 : 50;
  }

  get tax(): number {
    // No tax for now, but could be calculated based on region
    return 0;
  }

  get discount(): number {
    // Could be calculated based on promo codes, user tier, etc.
    return 0;
  }

  get processingFee(): number {
    if (!this.selectedPaymentPlan || !this.selectedPaymentMethod) return 0;
    return this.selectedPaymentPlan.processingFee || 0;
  }

  get total(): number {
    return (
      this.subtotal +
      this.shipping +
      this.tax +
      this.processingFee -
      this.discount
    );
  }

  get checkoutSummary(): CheckoutSummary {
    return {
      subtotal: this.subtotal,
      shipping: this.shipping,
      tax: this.tax,
      discount: this.discount,
      processingFee: this.processingFee,
      total: this.total,
      currency: 'GHS',
    };
  }

  get itemCount(): number {
    return this.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  get canProceedToPayment(): boolean {
    return (
      this.items.length > 0 &&
      this.selectedPaymentPlan !== null &&
      this.selectedPaymentMethod !== null &&
      this.termsAccepted &&
      this.privacyAccepted &&
      (this.selectedPaymentPlan.type === PaymentType.FULL_PAYMENT ||
        this.workDetails !== null)
    );
  }

  get validationResult(): CheckoutValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (this.items.length === 0) {
      errors.push('Cart is empty');
    }

    if (!this.selectedPaymentPlan) {
      errors.push('Payment plan not selected');
    }

    if (!this.selectedPaymentMethod) {
      errors.push('Payment method not selected');
    }

    if (!this.termsAccepted) {
      errors.push('Terms and conditions must be accepted');
    }

    if (!this.privacyAccepted) {
      errors.push('Privacy policy must be accepted');
    }

    if (
      this.selectedPaymentPlan?.type !== PaymentType.FULL_PAYMENT &&
      !this.workDetails
    ) {
      errors.push('Work details required for installment payments');
    }

    if (
      this.workDetails?.salary &&
      this.workDetails.salary < this.total * 0.3
    ) {
      warnings.push('Monthly payment may exceed recommended 30% of salary');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  // Actions
  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  clearCart(): void {
    runInAction(() => {
      this.items = [];
      this.selectedPaymentPlan = null;
      this.selectedPaymentMethod = null;
    });
  }

  async getPaymentPlanOptions(productId: string): Promise<void> {
    try {
      this.setLoading(true);
      this.setError(null);

      const product = await this.productRepository.getPaymentPlans(productId);
      if (!product) {
        throw new Error('Product not found');
      }
      runInAction(() => {
        this.paymentPlanResults = product;
      });
      // Assuming product has a retail_price field
    } catch (error) {
      this.setError(
        (error as any)['message'] || 'Failed to fetch product details'
      );
    } finally {
      this.setLoading(false);
    }
  }

  createCheckoutSession(productId: string, quantity: number): void {
    this.setLoading(true);
    this.setError(null);

    // This flow is not used in the current PaySmallSmall-first checkout,
    // but we explicitly avoid silently falling back to "pay in full".
    const paymentMethod = (this.selectedPaymentMethod as any)?.type ?? this.selectedPaymentMethod;
    if (!paymentMethod) {
      this.setError('Select PaySmallSmall to continue');
      this.setLoading(false);
      return;
    }
    if (String(paymentMethod).toUpperCase() !== 'PSS') {
      this.setError('PaySmallSmall is required to continue');
      this.setLoading(false);
      return;
    }

    this.createCheckoutSessionUseCase
      .execute({
        productId,
        quantity,
        paymentMethod: 'PSS',
        userId: 'current_user_id', // Replace with actual user ID from auth store
      })
      .then((session) => {
        runInAction(() => {
          this.checkoutSession = session;
        });
      })
      .catch((error) => {
        this.setError(error.message || 'Failed to create checkout session');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  selectPaymentPlan(plan: PaymentPlan): void {
    runInAction(() => {
      this.selectedPaymentPlan = plan;
      // Reset payment method when plan changes
      this.selectedPaymentMethod = null;
    });
  }

  selectPaymentMethod(method: any): void {
    this.selectedPaymentMethod = method;
  }

  // Work details
  updateWorkDetails(details: Partial<WorkDetails>): void {
    runInAction(() => {
      this.workDetails = this.workDetails
        ? { ...this.workDetails, ...details }
        : (details as WorkDetails);
    });
  }

  // Shipping address
  updateShippingAddress(address: Partial<ShippingAddress>): void {
    runInAction(() => {
      this.shippingAddress = this.shippingAddress
        ? { ...this.shippingAddress, ...address }
        : (address as ShippingAddress);
    });
  }

  // Consent management
  acceptTerms(accepted: boolean): void {
    this.termsAccepted = accepted;
  }

  acceptPrivacy(accepted: boolean): void {
    this.privacyAccepted = accepted;
  }

  setMarketingOptIn(optIn: boolean): void {
    this.marketingOptIn = optIn;
  }

  // Order creation
  async createOrder(
    paymentFrequency: string,
    selectedBreakDown: PaymentPlanDetails,
    keepMyPrice: boolean,
    durationMonths: number
  ): Promise<CheckoutSessionResult> {
    try {
      this.isCreatingOrder = true;
      this.setError(null);

      const orderPayload: CheckoutSessionParams = {
        productId: this.paymentPlanResults!.product.id,
        quantity: 1,
        paymentOption: 'PSS',
        paymentPlan: {
          durationMonths: durationMonths,
          frequency: paymentFrequency,
          kmpEnabled: keepMyPrice,
        },
        // deliveryOption: '',
        // deliveryAddressId: '',
      };

      const response = await this.checkoutService.createSession(
        orderPayload as any
      );
     
      if (response) {
        // Clear checkout data after successful order creation
        this.clearCheckout();
        runInAction(() => {
          this.checkoutSession = response;
        });
        return response;
      } else {
        throw new Error('Failed to create order');
      }
    } catch (error: any) {
      this.setError(error.message || 'Failed to create order');
      throw error;
    } finally {
      this.isCreatingOrder = false;
    }
  }

  async loadCheckoutSession(orderId: string): Promise<void> {
    try {
      this.setLoading(true);
      this.setError(null);
      const response = await this.checkoutService.getSession(orderId);
     
      runInAction(() => {
        this.currentCheckoutSession = response;
        this.isLoading = false;
        this.setError(null);
      });
    } catch (_error) {
      this.setError('Failed to load checkout session');
    } finally {
      this.setLoading(false);
    }
  }

  async listCollectionPoints(region: string): Promise<any[]> {
    return this.checkoutService.listCollectionPoints({ region });
  }

  /**
   * Finalizes the order after payment is confirmed.
   * Delivery params are optional - if not provided, order is created without delivery preference
   * (user can set delivery preference later when "ready to redeem").
   */
  async finalizeOrder(params?: {
    deliveryMethod?: 'COLLECTION_POINT' | 'HOME_DELIVERY';
    collectionPoint?: { id: string; code: string; name: string; region: string; city?: string };
    deliveryAddressId?: string;
    pickupNote?: string;
    deliveryNote?: string;
  }): Promise<any> {
    if (!this.currentCheckoutSession) {
      throw new Error('No active checkout session');
    }

    const sessionId = this.currentCheckoutSession.session_id;
    const paymentOption = (this.currentCheckoutSession as any)?.selected_plan?.payment_option;
    if (paymentOption !== 'PSS') {
      throw new Error('PaySmallSmall is required to create an order');
    }
    const paymentMethod = 'paysmallsmall';

    try {
      runInAction(() => {
        this.isFinalizingOrder = true;
        this.error = null;
      });

      // Build delivery preferences if collection point is provided
      const deliveryPreferences =
        params?.deliveryMethod === 'COLLECTION_POINT' && params?.collectionPoint
          ? {
              collection_point: {
                id: params.collectionPoint.id,
                code: params.collectionPoint.code,
                name: params.collectionPoint.name,
                region: params.collectionPoint.region,
                city: params.collectionPoint.city,
              },
            }
          : undefined;

      const order = await this.checkoutService.createOrderFromCheckoutSession({
        checkoutSessionId: sessionId,
        paymentMethod,
        deliveryMethod: params?.deliveryMethod,
        deliveryAddressId: params?.deliveryAddressId,
        deliveryPreferences,
        pickupNote: params?.pickupNote || null,
        deliveryNote: params?.deliveryNote || null,
      });

      runInAction(() => {
        this.createdOrder = order;
      });

      return order;
    } finally {
      runInAction(() => {
        this.isFinalizingOrder = false;
      });
    }
  }
  
  // Validation helpers
  validateWorkDetails(): string[] {
    const errors: string[] = [];

    if (!this.workDetails) {
      return ['Work details are required'];
    }

    if (!this.workDetails.employerName) {
      errors.push('Employer name is required');
    }

    if (!this.workDetails.workAddress) {
      errors.push('Work address is required');
    }

    if (!this.workDetails.salary || this.workDetails.salary <= 0) {
      errors.push('Valid salary is required');
    }

    if (!this.workDetails.workPhone) {
      errors.push('Work phone is required');
    }

    return errors;
  }

  validateShippingAddress(): string[] {
    const errors: string[] = [];

    if (!this.shippingAddress) {
      return ['Shipping address is required'];
    }

    if (!this.shippingAddress.firstName) {
      errors.push('First name is required');
    }

    if (!this.shippingAddress.lastName) {
      errors.push('Last name is required');
    }

    if (!this.shippingAddress.street) {
      errors.push('Street address is required');
    }

    if (!this.shippingAddress.city) {
      errors.push('City is required');
    }

    if (!this.shippingAddress.region) {
      errors.push('Region is required');
    }

    return errors;
  }

  // Reset and cleanup
  clearCheckout(): void {
    runInAction(() => {
      this.items = [];
      this.selectedPaymentPlan = null;
      this.selectedPaymentMethod = null;
      this.workDetails = null;
      this.shippingAddress = null;
      this.termsAccepted = false;
      this.privacyAccepted = false;
      this.marketingOptIn = false;
      this.error = null;
      this.isLoading = false;
    });
    this.initializeDefaultPlans();
  }

  reset(): void {
    this.clearCheckout();
  }

  // Payment plan management
  private initializeDefaultPlans(): void {
    this.availablePaymentPlans = [
      {
        type: PaymentType.FULL_PAYMENT,
        totalAmount: 0, // Will be calculated dynamically
        processingFee: 0,
      },
    ];
  }

  private generateArrayOfDays(
    duration: number,
    intervalDays: number
  ): number[] {
    const days: number[] = [];
    for (let i = 0; i < duration; i++) {
      days.push(i * intervalDays);
    }
    return days;
  }

  private getDurationInDays(duration: number, frequency: string): number {
    switch (frequency.toLowerCase()) {
      case 'daily':
        return duration;
      case 'weekly':
        return duration * 7;
      case 'monthly':
        return duration * 30;
      default:
        return duration * 7;
    }
  }
}

export { CheckoutStore };
